import React, { Component, useState } from 'react';
import { Link } from 'react-router-dom';
import DesignSection from './designSection';
import SectionBanner from './sectionBanner';
import SectionPageHeader from './sectionPageHeader';

const GREEN = '#4CAF50';
const RED = '#ED5C6D';

function PracticeRow({ type, children }) {
  const isDo = type === 'do';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
      <span
        className="material-symbols-rounded"
        style={{ fontSize: '20px', flexShrink: 0, color: isDo ? GREEN : RED, lineHeight: '1.4' }}
      >
        {isDo ? 'check_circle' : 'cancel'}
      </span>
      <p style={{ margin: 0 }}>{children}</p>
    </div>
  );
}

function FamilyHeader({ id, number, title, thesis, note }) {
  return (
    <div
      id={id}
      className="no-left-padding no-right-padding"
      style={{ borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '60px', marginTop: '60px', marginBottom: '40px' }}
    >
      <h4 style={{ margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }} className="primary-color-dark">
        <span className="primary-color">{number}</span>&nbsp;&nbsp;{title}
      </h4>
      <p className="subheading primary-color" style={{ fontStyle: 'italic', marginTop: '6px', marginBottom: 0 }}>{thesis}</p>
      {note && <p style={{ marginTop: '8px', marginBottom: 0, maxWidth: '720px' }}>{note}</p>}
    </div>
  );
}

function Principle({ title, summary, dos, donts, link }) {
  return (
    <div className="row tiny-padding no-left-padding no-right-padding">
      <div className="small-12 medium-5 small-padding no-top-padding no-bottom-padding no-left-padding">
        <h4 style={{ margin: 0 }}>{title}</h4>
        <p className="subheading" style={{ marginTop: '8px' }}>{summary}</p>
        {link && (
          <div style={{ marginTop: '16px' }}>
            <LinkButton to={link.to} label={link.label} />
          </div>
        )}
      </div>
      <div className="small-12 medium-7 small-padding no-top-padding no-bottom-padding no-left-padding">
        <div className="small-padding primary-background-subtle" style={{ borderRadius: '12px' }}>
          <h6 style={{ margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.7 }}>
            {'In Practice'}
          </h6>
          {dos.map((d, i) => <PracticeRow key={'do' + i} type="do">{d}</PracticeRow>)}
          {donts.map((d, i) => <PracticeRow key={'dont' + i} type="dont">{d}</PracticeRow>)}
        </div>
      </div>
    </div>
  );
}

function LinkButton({ to, label }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: hovered ? '#0D4753' : '#006E85',
        color: '#fff',
        textDecoration: 'none',
        padding: '12px 20px',
        borderRadius: '8px',
        fontWeight: 600,
        transition: 'background-color 0.15s ease',
      }}
    >
      {label}
      <span className="material-symbols-rounded" style={{ fontSize: '20px' }}>arrow_forward</span>
    </Link>
  );
}

function ResourceCard({ title, description, icon, href, site, cover }) {
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(false);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
    >
      <div
        className="tiny-padding"
        style={{
          borderRadius: '8px',
          position: 'relative',
          minHeight: '150px',
          paddingBottom: '36px',
          backgroundColor: active ? 'rgba(0, 110, 133, 0.12)' : hovered ? 'rgba(0, 110, 133, 0.08)' : 'transparent',
          boxShadow: hovered ? 'none' : '0 2px 8px rgba(0,0,0,0.12)',
          transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
          transition: 'transform 0.2s ease, background-color 0.15s ease, box-shadow 0.2s ease',
          color: '#006E85',
        }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          {cover && (
            <img
              src={cover}
              alt={`${title} book cover`}
              loading="lazy"
              style={{ width: '44px', height: 'auto', borderRadius: '3px', flexShrink: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }}
            />
          )}
          <div>
            <h6 style={{ margin: 0, textTransform: 'uppercase' }}>{title}</h6>
            {description && (
              <p className="micro-top-margin" style={{ marginBottom: 0 }}>{description}</p>
            )}
          </div>
        </div>
        <div style={{ position: 'absolute', bottom: '12px', right: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          {site && (
            <span
              style={{
                fontSize: '11px',
                lineHeight: 1,
                fontWeight: 600,
                color: '#006E85',
              }}
            >
              {site}
            </span>
          )}
          <span className="material-symbols-rounded" style={{ fontSize: '20px', color: '#006E85' }}>
            {icon}
          </span>
        </div>
      </div>
    </a>
  );
}

const FAMILIES = [
  {
    id: 'simplicity-focus',
    number: '01',
    title: 'Simplicity & Focus',
    thesis: 'Design for the 80% by optimizing the most common workflows.',
    note: 'Remove friction from frequent tasks, reduce distractions, and reserve complexity for the moments that truly need it.',
    principles: [
      {
        title: 'Complexity is opt-in',
        summary: 'Experiences start simple and only expose depth when the user goes looking for it. Every powerful tool needs an approachable, low cognitive load representation of itself — and that simple version is what people meet first.',
        dos: [
          'Lead with a simple, focused first screen and reveal depth progressively.',
          'Choose smart defaults so most users never touch advanced settings.',
        ],
        donts: [
          'Expose every option, setting, and edge case up front.',
        ],
      },
      {
        title: 'Design directs attention',
        summary: 'Use hierarchy, contrast, and emphasis so the most important thing is the most noticeable. Every screen should have one obvious primary action and a clear path through it.',
        dos: [
          'Give each screen a single, clear primary action.',
          'Use size, contrast, and placement to rank what matters.',
        ],
        donts: [
          'Let every element compete for attention with equal weight.',
        ],
      },
      {
        title: 'Reduce the cost of every decision',
        summary: 'Fewer, clearer options mean faster, more confident decisions. Limit and group the choices on screen, and break complex tasks into smaller, manageable steps.',
        dos: [
          'Limit and group the choices presented at once.',
          'Chunk complex flows into smaller steps.',
        ],
        donts: [
          'Present long, flat lists of equally-weighted options.',
        ],
      },
    ],
  },
  {
    id: 'intuitive-cohesive',
    number: '02',
    title: 'Intuitive & Cohesive',
    thesis: 'Create intuitive experiences through consistency.',
    note: 'Reuse familiar patterns, make the right actions obvious, and design every feature as part of a cohesive whole.',
    principles: [
      {
        title: 'One unifying mind',
        summary: 'The entire product should look and feel like it came from a single mind. B2B products tend to fragment as many teams ship their own piece — so we back up and view each feature through one holistic lens.',
        dos: [
          'Reuse shared components, patterns, and language across teams.',
          'Review new features against the whole product, not in isolation.',
        ],
        donts: [
          'Build one-off UI per feature that drifts from the rest of the product.',
        ],
      },
      {
        title: 'Training & documentation is a last resort',
        summary: 'Nobody wants to spend time learning a new application. If users need training or documentation to understand our product, it isn’t intuitive enough — and intuitive is what earns viral adoption.',
        dos: [
          'Lean on familiar patterns.',
          'Make the right action the obvious one through layout and defaults.',
        ],
        donts: [
          'Rely on a manual, tooltip tour, or onboarding to make it usable.',
        ],
      },
    ],
  },
  {
    id: 'communicate-forgive',
    number: '03',
    title: 'Communicate & Forgive',
    thesis: 'Keep people informed without overwhelming them.',
    note: "Communicate what's happening, make important information easy to find, and provide feedback when it matters.",
    principles: [
      {
        title: 'Give immediate feedback',
        summary: 'The system should keep people informed in a timely manner — nothing happens silently. Responses should feel immediate, ideally under the ~400ms where attention starts to wander.',
        dos: [
          'Confirm every action with clear, immediate feedback.',
          'Show progress, loading, and saved states explicitly.',
        ],
        donts: [
          'Leave users guessing whether something worked.',
        ],
      },
      {
        title: 'Make it safe to explore',
        summary: 'Favor reversible actions, easy undo, and graceful recovery over blocking confirmations. Mistakes should be cheap, not scary — so people feel free to learn by doing.',
        dos: [
          'Prefer undo over an “are you sure?” dialog.',
          'Write errors that explain how to recover.',
        ],
        donts: [
          'Make destructive actions easy and irreversible.',
        ],
      },
      {
        title: 'Quiet by default',
        summary: 'The UX should be able to instruct users without instructions, and warn only when it’s truly necessary. A good interface guides through layout and affordance — not interruptions.',
        dos: [
          'Let the design teach; reserve warnings for genuinely destructive or irreversible actions.',
          'Guide attention with hierarchy and placement instead of alerts.',
        ],
        donts: [
          'Interrupt with modals, banners, and alerts the user must dismiss to proceed.',
        ],
      },
    ],
  },
  {
    id: 'grounded-in-people',
    number: '04',
    title: 'Grounded in People',
    thesis: 'Design for real people solving real problems.',
    note: 'Start with user needs, understand the context behind their decisions, and let research guide what you build.',
    principles: [
      {
        title: 'Start with the user',
        summary: "Ground decisions in real user needs and validate them with testing, not assumptions. The best design comes from understanding the problem deeply before committing to a solution.",
        dos: [
          'Talk to and observe real users before deciding.',
          'Prototype and test early, then iterate.',
        ],
        donts: [
          'Ship based on internal opinion alone.',
        ],
      },
      {
        title: 'Design for everyone',
        summary: 'Meet contrast, target-size, keyboard, and screen-reader needs by default. Accessible design isn’t a special case — it’s simply better, more usable design for all.',
        dos: [
          'Meet WCAG contrast and target-size minimums.',
          'Support keyboard and screen-reader use from the start.',
        ],
        donts: [
          'Treat accessibility as a final-pass afterthought.',
        ],
      },
    ],
  },
  {
    id: 'data-visualization',
    number: '05',
    title: 'Data Visualization',
    thesis: 'Turn complex data into clear, honest stories.',
    note: 'Charts are where our product’s value becomes visible. Keep them accurate, legible, and honest, and lean on the shared chart system so every visualization feels part of the same product.',
    principles: [
      {
        title: 'Clarity over decoration',
        summary: 'A chart exists to reveal the data, not to impress. Make it legible and truthful, and use the shared chart system so visualizations feel consistent across the product.',
        dos: [
          'Use the shared chart colors, types, and formatting.',
          'Label axes, units, and sources so a chart stands on its own.',
        ],
        donts: [
          'Distort scales or truncate axes in ways that mislead.',
          'Add chartjunk — 3D, heavy gridlines, or decoration that obscures the data.',
        ],
        link: { to: '/design/charts', label: 'Explore the full Data Visualization guidelines' },
      },
    ],
  },
];

const RESOURCES = [
  {
    title: '10 Usability Heuristics',
    description: 'Jakob Nielsen’s ten core principles for usable interface design.',
    icon: 'open_in_new',
    site: 'nngroup.com',
    href: 'https://www.nngroup.com/articles/ten-usability-heuristics/',
  },
  {
    title: 'Psychology of Design',
    description: '100+ cognitive biases and principles that shape user behavior.',
    icon: 'open_in_new',
    site: 'growth.design',
    href: 'https://growth.design/psychology',
  },
  {
    title: 'Design Thinking',
    description: 'The five-stage process: empathize, define, ideate, prototype, test.',
    icon: 'open_in_new',
    site: 'interaction-design.org',
    href: 'https://www.interaction-design.org/literature/topics/design-thinking',
  },
  {
    title: 'Dieter Rams’ 10 Principles',
    description: 'The timeless tenets of good design that shaped modern products.',
    icon: 'open_in_new',
    site: 'vitsoe.com',
    href: 'https://www.vitsoe.com/us/about/good-design',
  },
  {
    title: 'Pareto Principle',
    description: 'Roughly 80% of effects come from 20% of causes — design for the vital few.',
    icon: 'open_in_new',
    site: 'lawsofux.com',
    href: 'https://lawsofux.com/pareto-principle/',
  },
  {
    title: 'Tesler’s Law',
    description: 'Conservation of complexity — we absorb it so the user doesn’t.',
    icon: 'open_in_new',
    site: 'lawsofux.com',
    href: 'https://lawsofux.com/teslers-law/',
  },
  {
    title: 'Hick’s Law',
    description: 'More choices mean slower decisions; fewer options lower the load.',
    icon: 'open_in_new',
    site: 'lawsofux.com',
    href: 'https://lawsofux.com/hicks-law/',
  },
  {
    title: 'Von Restorff Effect',
    description: 'The element that stands out is the one people remember.',
    icon: 'open_in_new',
    site: 'lawsofux.com',
    href: 'https://lawsofux.com/von-restorff-effect/',
  },
  {
    title: 'Jakob’s Law',
    description: 'Users expect our product to work like the others they know.',
    icon: 'open_in_new',
    site: 'lawsofux.com',
    href: 'https://lawsofux.com/jakobs-law/',
  },
  {
    title: 'Occam’s Razor',
    description: 'Among competing designs, the simplest that works wins.',
    icon: 'open_in_new',
    site: 'lawsofux.com',
    href: 'https://lawsofux.com/occams-razor/',
  },
  {
    title: 'The Design of Everyday Things',
    description: 'Don Norman’s classic on affordances, feedback, and human-centered design.',
    icon: 'open_in_new',
    site: 'amazon.com',
    cover: 'https://covers.openlibrary.org/b/isbn/9780465050659-M.jpg?default=false',
    href: 'https://www.amazon.com/Design-Everyday-Things-Revised-Expanded/dp/0465050654',
  },
  {
    title: 'The Mom Test',
    description: 'How to talk to users and learn what they really need — without the lies.',
    icon: 'open_in_new',
    site: 'amazon.com',
    cover: 'https://covers.openlibrary.org/b/isbn/9781492180746-M.jpg?default=false',
    href: 'https://www.amazon.com/Mom-Test-customers-business-everyone/dp/1492180742',
  },
];

export default class MethodologySection extends Component {
  render() {
    return (
      <div className="sectionContainer full-width">
        <SectionBanner image="/images/background-lines-gradient-purple.webp" title="Methodology" />
        <SectionPageHeader description="Design is more than the interface – it's a shared way of solving problems. Together, these principles shape every product, feature, and interaction we create, helping us build experiences that are simple, cohesive, and intuitive. No manual required." />
        <DesignSection
          title={'Our Design Principles'}>
          {FAMILIES.map((family) => (
            <div key={family.number}>
              <FamilyHeader
                id={family.id}
                number={family.number}
                title={family.title}
                thesis={family.thesis}
                note={family.note}
              />
              {family.principles.map((p) => (
                <Principle
                  key={p.title}
                  title={p.title}
                  summary={p.summary}
                  dos={p.dos}
                  donts={p.donts}
                  link={p.link}
                />
              ))}
            </div>
          ))}
        </DesignSection>
        <DesignSection
          id={'resources'}
          title={'Resources'}
          style={{ borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '60px', marginTop: '60px' }}
          subtitle={'Our principles align with established industry-standards. Want to dive deeper and learn more?'}>
          <div className="row">
            {RESOURCES.map((r, i) => (
              <div key={i} className="small-12 medium-6 large-4 tiny-padding no-left-padding">
                <ResourceCard title={r.title} description={r.description} icon={r.icon} href={r.href} site={r.site} cover={r.cover} />
              </div>
            ))}
          </div>
        </DesignSection>
      </div>
    );
  }
}
